import { For, Show, createEffect, createMemo, createSignal, onCleanup, type JSX } from 'solid-js'
import { COLUMN_WIDTH } from 'mormaii-web-presentation'
import Css from './table.module.scss'
import { Spinner } from '../ui'

/** O minimo que a tabela precisa saber de uma coluna. */
export type TableColumn = {
    field: string
    label: string
    sortable: boolean
    sorted: boolean
    descending: boolean
    onSort: () => Promise<void>

    /**
     * A coluna pode ser arrastada para outro lugar - e receber outra que se arraste.
     *
     * A tabela so avisa quem foi solto sobre quem; a ordem nova e quem a guarda sao
     * da apresentacao, que devolve as colunas ja reordenadas.
     */
    movable?: boolean
    onDrop?: (source: string) => void

    /**
     * `false` tira a borda arrastavel desta coluna. Quem decide e o modelo
     * (`layout.resizable`); isto so serve a view que queira recusar onde ele deixa.
     */
    resizable?: boolean
}

/**
 * A geometria que o MODELO da tabela decide - o `TableLayoutScope` da apresentacao
 * (19/10/2026). A tabela nao faz conta nenhuma: desenha as larguras e as presas que
 * recebe, mede a largura que o conteudo pede quando falta alguma, e avisa o que o
 * leitor fez com a borda. Quantas colunas ficam presas a esquerda tambem vem daqui.
 */
export type TableLayout = {
    widths: Record<string, number>
    offsets: Record<string, number>
    pinned: number
    pinnedWidth: number
    resizable: Record<string, boolean>
    measuredWithRows: boolean
    onMeasure: (widths: Record<string, number>, withRows: boolean) => void
    onResize: (field: string, width: number) => void
}

export function DataTable<Row>(props: {
    columns: TableColumn[]
    rows: Row[]
    /**
     * A celula de uma coluna, pelo CAMPO dela. A tabela a pede uma vez por campo e a
     * identifica por ele: mudar ou mover colunas reposiciona as celulas que ficam, e nao
     * as recria - o foco e o estado de cada uma sobrevivem.
     */
    cell: (row: Row, field: string) => JSX.Element
    loading?: boolean
    emptyText?: string
    /**
     * Prende a ULTIMA coluna na borda direita.
     *
     * Para a coluna de acoes: sem isso, so descobre que a acao existe quem rola a
     * tabela ate o fim - e em Empresas sao nove colunas antes dela. A celula presa
     * precisa de fundo proprio, senao o conteudo passa por baixo e aparece atraves.
     */
    pinLast?: boolean

    /**
     * Classe extra na LINHA, decidida pelo conteudo dela.
     *
     * Para destacar uma linha inteira sem que a tabela precise saber por que. Fundo
     * se muda pela variavel `--row-bg` (`[--row-bg:#f7f5ff]`), e nao por `bg-`: as
     * celulas presas (as primeiras e a `pinLast`) precisam de fundo OPACO proprio, senao
     * o conteudo passa por baixo delas - e um `bg-` na linha as deixaria brancas no
     * meio da faixa.
     */
    rowClass?: (row: Row) => string

    /**
     * A geometria do modelo da tabela. As colunas presas a esquerda sao as primeiras
     * `layout.pinned` - a hora no historico, "Atenção" e o nome na lista de
     * colaboradores (B-31): rolando de lado, nao se perde de vista de quem e a linha.
     * Ate ela chegar (o presenter ainda montando a tela), a tabela fica no layout
     * automatico do navegador, sem presas e sem borda arrastavel.
     */
    layout?: TableLayout
}): JSX.Element {
    let table: HTMLTableElement | undefined
    let scroller: HTMLDivElement | undefined
    const pinned = () => props.layout?.pinned ?? 0
    const fields = createMemo(() => props.columns.map((column) => column.field))

    /*
     * O arrastar de colunas, por eventos de PONTEIRO (relato de 19/10/2026).
     *
     * Era o arrastar nativo do navegador (`draggable`), e ele nao comecava: quase todo
     * o cabecalho e o botao de ordenar, e o Firefox - e o Safari em parte dos casos -
     * nao inicia arrasto nativo a partir de um botao. Com ponteiro, o gesto e o mesmo
     * em todo navegador e no toque. O arrasto so comeca depois de alguns pixels: antes
     * disso o gesto ainda e um clique, e o clique ordena.
     *
     * A coluna sob o ponteiro e achada pela posicao (`elementFromPoint`), e so vale
     * coluna DESTA tabela que aceite receber - uma de outra tabela da tela nao conta.
     */
    const [dragging, setDragging] = createSignal<string>()
    const [over, setOver] = createSignal<string>()
    const indexOf = (field: string | undefined) => fields().indexOf(field ?? '')
    const columnOf = (field: string | undefined) => props.columns.find((column) => column.field === field)
    const accepts = (column: TableColumn | undefined) =>
        !!column?.movable && dragging() !== undefined && dragging() !== column.field
    const endDrag = () => {
        setDragging(undefined)
        setOver(undefined)
        document.body.classList.remove(Css.draggingBody)
    }

    /*
     * O gesto em curso, para encerrar se a tabela sair da tela no meio dele (o Voltar do
     * navegador com o mouse apertado): sem isto, os ouvintes em `window`/`document`
     * ficavam, e soltar o botao entregaria o gesto a uma tela que ja saiu.
     */
    let abortGesture: (() => void) | undefined
    onCleanup(() => abortGesture?.())

    const startPointerDrag = (event: PointerEvent, column: TableColumn) => {
        if (!column.movable || event.button !== 0) return
        // A borda da largura tem o gesto dela.
        if ((event.target as Element).closest('[role=separator]')) return

        const startX = event.clientX
        const startY = event.clientY
        let lastX = startX
        let lastY = startY

        /*
         * Perto da borda da caixa que rola, ela anda sozinha, uma coluna por vez: a
         * tabela e mais larga que a tela, e a coluna de destino pode estar fora dela.
         * A borda da esquerda conta a partir das presas, que nao saem do lugar.
         */
        let rolling: ReturnType<typeof setInterval> | undefined
        const stopRolling = () => {
            clearInterval(rolling)
            rolling = undefined
        }
        const edgeDirection = () => {
            if (!scroller) return 0
            const box = scroller.getBoundingClientRect()
            const pinnedEdge = box.left + (settled() ? (props.layout?.pinnedWidth ?? 0) : 0)
            if (lastX > box.right - EDGE_ZONE) return 1
            if (lastX < pinnedEdge + EDGE_ZONE && lastX > box.left) return -1
            return 0
        }
        const followPointer = () => {
            const head = document.elementFromPoint(lastX, lastY)?.closest('th[data-field]')
            const target = head && table?.contains(head) ? columnOf((head as HTMLElement).dataset.field) : undefined
            setOver(accepts(target) ? target!.field : undefined)
        }
        const roll = () => {
            const direction = edgeDirection()
            if (direction === 0 || !scroller) return stopRolling()
            scroller.scrollBy({ left: direction * ROLL_STEP })
            // A coluna sob o ponteiro mudou sem o ponteiro se mexer.
            requestAnimationFrame(followPointer)
        }

        const move = (moved: PointerEvent) => {
            lastX = moved.clientX
            lastY = moved.clientY
            if (dragging() === undefined) {
                if (Math.hypot(moved.clientX - startX, moved.clientY - startY) < DRAG_THRESHOLD) return
                setDragging(column.field)
                document.body.classList.add(Css.draggingBody)
            }
            moved.preventDefault()
            followPointer()
            if (edgeDirection() !== 0 && rolling === undefined) {
                roll()
                rolling = setInterval(roll, ROLL_INTERVAL)
            }
        }

        const up = () => {
            stopRolling()
            window.removeEventListener('pointermove', move)
            window.removeEventListener('pointerup', up)
            window.removeEventListener('pointercancel', cancel)
            abortGesture = undefined
            const source = dragging()
            const target = columnOf(over())
            if (source === undefined) return

            // Foi arrasto, e nao clique: o clique que o navegador manda em seguida nao ordena.
            window.addEventListener('click', swallow, { capture: true, once: true })
            setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0)
            endDrag()
            if (target && target.field !== source) target.onDrop?.(source)
        }

        const cancel = () => {
            stopRolling()
            window.removeEventListener('pointermove', move)
            window.removeEventListener('pointerup', up)
            window.removeEventListener('pointercancel', cancel)
            abortGesture = undefined
            endDrag()
        }

        abortGesture?.()
        abortGesture = cancel
        window.addEventListener('pointermove', move)
        window.addEventListener('pointerup', up)
        window.addEventListener('pointercancel', cancel)
    }

    /*
     * A LARGURA das colunas (relato de 18/10/2026, decidido no mesmo dia).
     *
     * Quem decide a largura e o modelo da tabela; a de partida, porem, so a view sabe: e
     * a que o conteudo pede. A tabela desenha uma vez no layout automatico do navegador,
     * com o dado, le a largura de cada cabecalho, entrega ao modelo (`onMeasure`) e dali
     * em diante fica em layout fixo. Uma pagina seguinte com um nome mais comprido NAO
     * alarga a coluna - o texto sai com reticencias e o leitor arrasta a borda.
     *
     * Mede-se de novo quando aparece uma coluna que o modelo ainda nao tem (o seletor de
     * colunas acrescentou uma) e quando o primeiro dado chega a uma tabela medida vazia
     * - medida so com o cabecalho, a coluna cortaria o nome de todo mundo.
     */
    const known = (field: string) => props.layout?.widths[field] !== undefined
    // "Ja mediu com dado" e do modelo, e nao daqui: voltar a tela nao mede de novo.
    const settled = createMemo(
        () => fields().every(known) && (!!props.layout?.measuredWithRows || props.rows.length === 0)
    )

    createEffect(() => {
        if (!props.layout || settled() || (props.loading && props.rows.length === 0)) return
        // Um quadro no layout automatico, e so entao a medida: antes dele o navegador
        // ainda mostra as larguras do layout fixo.
        const frame = requestAnimationFrame(() => {
            if (!table) return
            const widths: Record<string, number> = {}
            for (const head of table.querySelectorAll<HTMLTableCellElement>('thead th[data-field]')) {
                widths[head.dataset.field!] = head.getBoundingClientRect().width
            }
            props.layout?.onMeasure(widths, props.rows.length > 0)
        })
        onCleanup(() => cancelAnimationFrame(frame))
    })

    /*
     * O arrasto da borda em curso. A largura acompanha o ponteiro na previa, e vai ao
     * modelo uma vez so, ao soltar - cada pixel do caminho nao e uma escolha do leitor.
     * A previa fica um instante depois de entregue, ate a largura guardada voltar pelo
     * scope (o redesenho e em lote), para a coluna nao piscar na largura velha.
     */
    const [preview, setPreview] = createSignal<Record<string, number>>({})
    const [resizing, setResizing] = createSignal<string>()

    const commitResize = (field: string) => {
        const width = preview()[field]
        if (width === undefined) return
        props.layout?.onResize(field, width)
        // So esta coluna, e so se ninguem a arrastou de novo nesse meio tempo: limpar a
        // previa inteira apagaria a de outro arrasto que comecou depois.
        setTimeout(() => {
            setPreview((old) => {
                if (old[field] !== width) return old
                const rest = { ...old }
                delete rest[field]
                return rest
            })
        }, 120)
    }

    const startResize = (event: MouseEvent | TouchEvent, field: string) => {
        const pointX = (moved: MouseEvent | TouchEvent) =>
            'touches' in moved ? moved.touches[0]?.clientX : (moved as MouseEvent).clientX
        const startX = pointX(event)
        const startWidth = sizeOf(field)
        if (startX === undefined || startWidth === undefined) return
        setResizing(field)

        const move = (moved: MouseEvent | TouchEvent) => {
            const x = pointX(moved)
            if (x === undefined) return
            const width = Math.min(COLUMN_WIDTH.max, Math.max(COLUMN_WIDTH.min, startWidth + x - startX))
            setPreview((old) => ({ ...old, [field]: width }))
        }
        const stop = () => {
            document.removeEventListener('mousemove', move)
            document.removeEventListener('mouseup', end)
            document.removeEventListener('touchmove', move)
            document.removeEventListener('touchend', end)
            document.removeEventListener('touchcancel', stop)
            abortGesture = undefined
            setResizing(undefined)
        }
        const end = () => {
            stop()
            commitResize(field)
        }

        abortGesture?.()
        // Saiu da tela no meio do arrasto: nada e entregue.
        abortGesture = stop
        document.addEventListener('mousemove', move)
        document.addEventListener('mouseup', end)
        document.addEventListener('touchmove', move)
        document.addEventListener('touchend', end)
        // O navegador tomou o toque (rolagem, gesto do sistema): nada e entregue.
        document.addEventListener('touchcancel', stop)
    }
    const canResize = (column: TableColumn): boolean =>
        !!props.layout?.resizable[column.field] && column.resizable !== false

    /*
     * Onde cada coluna presa encosta, e a largura do bloco preso inteiro, vem do modelo.
     * O bloco vai para a caixa que rola (`--pin-width`): e ali que a rolagem encaixa a
     * coluna seguinte, rente as presas.
     */
    // So com a geometria: antes dela o `left` de cada presa ainda nao chegou, e presas
    // todas em `left: 0` ficariam umas sobre as outras ao rolar.
    const stuckLeft = (index: number) => settled() && index < pinned()
    const stuckEdge = (index: number) => settled() && index === pinned() - 1
    const stuckRight = (index: number) => !!props.pinLast && index === props.columns.length - 1
    const sizeOf = (field: string): number | undefined => preview()[field] ?? props.layout?.widths[field]
    const totalWidth = () => fields().reduce((sum, field) => sum + (sizeOf(field) ?? 0), 0)

    const stuckStyle = (index: number): JSX.CSSProperties | undefined => {
        if (!settled() || !stuckLeft(index)) return undefined
        return { left: `${props.layout?.offsets[fields()[index]!] ?? 0}px` }
    }
    const widthStyle = (field: string): JSX.CSSProperties | undefined => {
        // No quadro de medida, nenhuma: ali a largura e a que o conteudo pede.
        const width = settled() ? sizeOf(field) : undefined
        return width === undefined ? undefined : { width: `${width}px` }
    }

    /*
     * A coluna de SOBRA, sem largura: no layout fixo ela fica com o que falta para a
     * tabela encher a caixa, e as outras ficam exatamente na largura delas. Sem ela,
     * o navegador repartiria a sobra entre todas, e a coluna estreitada pelo leitor
     * voltaria a alargar. Fica antes da ultima quando a ultima e presa a direita.
     */
    const fillerAt = () => (props.pinLast ? props.columns.length - 1 : props.columns.length)
    const Filler = (filler: { head?: boolean }) => (
        <Show when={settled()}>{filler.head ? <th class={Css.filler} /> : <td class={Css.filler} />}</Show>
    )

    return (
        <div class={Css.frame}>
            {/*
                A rolagem horizontal e DESTA caixa, e nao da pagina: sao ate quinze
                colunas de medicao, e num laptop elas nao cabem. Deixar a pagina
                rolar de lado levaria o menu e o cabecalho junto.
            */}
            <div
                ref={scroller}
                class={Css.scroller}
                style={settled() ? { '--pin-width': `${props.layout?.pinnedWidth ?? 0}px` } : undefined}
            >
                <table
                    ref={table}
                    class={Css.table}
                    classList={{ [Css.fixed]: settled(), [Css.measuring]: !settled() }}
                    style={settled() ? { 'min-width': `${totalWidth()}px` } : undefined}
                >
                    <thead>
                        <tr class={Css.head}>
                            <For each={fields()}>
                                {(field, index) => {
                                    // Pelo CAMPO, e nao pelo objeto: a mesma coluna remontada pela apresentacao
                                    // continua o mesmo cabecalho, e so o que mudou nele e redesenhado.
                                    const column = () => columnOf(field) ?? GONE
                                    return (
                                        <>
                                            <Show when={index() === fillerAt()}>
                                                <Filler head />
                                            </Show>
                                            <th
                                                data-field={field}
                                                class={Css.column}
                                                style={{ ...widthStyle(field), ...stuckStyle(index()) }}
                                                title={
                                                    column().movable
                                                        ? 'Arraste para mudar a coluna de lugar'
                                                        : undefined
                                                }
                                                classList={{
                                                    [Css.stuckLeft]: stuckLeft(index()),
                                                    [Css.stuckEdge]: stuckEdge(index()),
                                                    [Css.stuckRight]: stuckRight(index()),
                                                    [Css.movable]: column().movable,
                                                    [Css.dragging]: dragging() === field,
                                                    // A marca fica do lado em que a coluna vai cair.
                                                    [Css.dropBefore]: over() === field && indexOf(dragging()) > index(),
                                                    [Css.dropAfter]: over() === field && indexOf(dragging()) < index()
                                                }}
                                                onPointerDown={(event) => startPointerDrag(event, column())}
                                            >
                                                <Show when={column().movable}>
                                                    <GripMark />
                                                </Show>
                                                <Show
                                                    when={column().sortable}
                                                    fallback={<span class={Css.label}>{column().label}</span>}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => void column().onSort()}
                                                        class={Css.sortable}
                                                        classList={{ [Css.sorted]: column().sorted }}
                                                    >
                                                        {column().label}
                                                        <SortMark
                                                            sorted={column().sorted}
                                                            descending={column().descending}
                                                        />
                                                    </button>
                                                </Show>
                                                <Show when={settled() && canResize(column())}>
                                                    <ResizeHandle
                                                        label={column().label}
                                                        active={resizing() === field}
                                                        onStart={(event) => startResize(event, field)}
                                                    />
                                                </Show>
                                            </th>
                                        </>
                                    )
                                }}
                            </For>
                            <Show when={fillerAt() === props.columns.length}>
                                <Filler head />
                            </Show>
                        </tr>
                    </thead>

                    <tbody>
                        <For each={props.rows}>
                            {(row) => (
                                <tr class={`${Css.row} ${props.rowClass?.(row) ?? ''}`}>
                                    <For each={fields()}>
                                        {(field, index) => (
                                            <>
                                                <Show when={index() === fillerAt()}>
                                                    <Filler />
                                                </Show>
                                                <td
                                                    class={Css.cell}
                                                    style={stuckStyle(index())}
                                                    classList={{
                                                        [Css.stuckLeft]: stuckLeft(index()),
                                                        [Css.stuckEdge]: stuckEdge(index()),
                                                        [Css.stuckRight]: stuckRight(index())
                                                    }}
                                                >
                                                    {props.cell(row, field)}
                                                </td>
                                            </>
                                        )}
                                    </For>
                                    <Show when={fillerAt() === props.columns.length}>
                                        <Filler />
                                    </Show>
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
            </div>

            <Show when={props.rows.length === 0 && !props.loading}>
                <p class={Css.empty}>{props.emptyText ?? 'Nada para mostrar'}</p>
            </Show>

            <Show when={props.loading}>
                <div class={Css.veil}>
                    <Spinner class={Css.spinner} />
                </div>
            </Show>
        </div>
    )
}

/**
 * A coluna que acabou de sair: o cabecalho dela ainda existe no instante entre a lista
 * mudar e ele ser descartado, e nao pode quebrar lendo uma coluna que nao esta mais la.
 */
const GONE: TableColumn = {
    field: '',
    label: '',
    sortable: false,
    sorted: false,
    descending: false,
    onSort: async () => undefined
}

/** Quanto o ponteiro anda antes de o gesto deixar de ser clique e virar arrasto. */
const DRAG_THRESHOLD = 5

/**
 * A rolagem durante o arrasto: a faixa junto a borda que a dispara, e o passo. O passo
 * e de uma coluna estreita - a rolagem encaixa na coluna seguinte sozinha (`scroll-snap`).
 */
const EDGE_ZONE = 48
const ROLL_STEP = 160
const ROLL_INTERVAL = 350

function swallow(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
}

/**
 * A borda arrastavel, no lado direito do cabecalho.
 *
 * O cabecalho de uma coluna de medicao tambem se ARRASTA (para mudar de lugar). Segurar
 * a borda nao pode comecar aquele arrasto - quem o comeca olha se o ponteiro desceu
 * aqui -, e o `mousedown` sem o comportamento padrao nao seleciona texto.
 */
function ResizeHandle(props: {
    label: string
    active: boolean
    onStart: (event: MouseEvent | TouchEvent) => void
}): JSX.Element {
    return (
        <span
            role="separator"
            aria-orientation="vertical"
            aria-label={`Ajustar a largura de ${props.label}`}
            title="Arraste para mudar a largura"
            class={Css.resizer}
            classList={{ [Css.resizing]: props.active }}
            onMouseDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
                props.onStart(event)
            }}
            onTouchStart={(event) => {
                event.stopPropagation()
                props.onStart(event)
            }}
            onClick={(event) => event.stopPropagation()}
        />
    )
}

/** A pega de arrasto: duas colunas de pontos, como em toda lista que se reordena. */
function GripMark(): JSX.Element {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" class={Css.grip} aria-hidden="true">
            <circle cx="9" cy="6" r="1.6" />
            <circle cx="15" cy="6" r="1.6" />
            <circle cx="9" cy="12" r="1.6" />
            <circle cx="15" cy="12" r="1.6" />
            <circle cx="9" cy="18" r="1.6" />
            <circle cx="15" cy="18" r="1.6" />
        </svg>
    )
}

function SortMark(props: { sorted: boolean; descending: boolean }): JSX.Element {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            class={Css.mark}
        >
            <Show when={props.sorted && props.descending} fallback={<path d="m6 15 6-6 6 6" />}>
                <path d="m6 9 6 6 6-6" />
            </Show>
        </svg>
    )
}
